/**
 * API Client
 * Handles authentication headers and error handling
 */

const API_BASE = "/api";

// Dev bypass: skip 401 redirect when VITE_DEV_BYPASS=true
const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS === "true";

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  if (DEV_BYPASS) return "dev-bypass-token";
  try {
    const stored = localStorage.getItem("auth");
    if (!stored) return null;
    const { tokens } = JSON.parse(stored);
    return tokens?.token ?? null;
  } catch {
    return null;
  }
}

/**
 * Fetch wrapper that adds auth headers and handles 401 errors
 */
async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // Handle 401 - clear auth and redirect to login (skipped in dev bypass mode)
  if (response.status === 401) {
    if (!DEV_BYPASS) {
      localStorage.removeItem("auth");
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message ?? `API error: ${response.status}`);
  }

  return response.json();
}

/**
 * Typed GET request
 */
export async function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: "GET" });
}

/**
 * Typed POST request
 */
export async function apiPost<T>(
  path: string,
  body?: Record<string, unknown>
): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Typed PUT request
 */
export async function apiPut<T>(
  path: string,
  body?: Record<string, unknown>
): Promise<T> {
  return apiFetch<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Typed DELETE request
 */
export async function apiDelete<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: "DELETE" });
}

// ─── Stripe / Billing ────────────────────────────────────────────────────────

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "incomplete";

export type SubscriptionTier = "free" | "basic" | "pro";

export interface SubscriptionSummary {
  status: SubscriptionStatus;
  trialEndDate: string | null;
  gracePeriodEnd: string | null;
  tier: SubscriptionTier;
}

export async function getSubscription(
  venueId: string
): Promise<ApiResponse<SubscriptionSummary>> {
  return apiGet<ApiResponse<SubscriptionSummary>>(`/stripe/subscription/${venueId}`);
}

export async function createCheckoutSession(body: {
  venueId: string;
}): Promise<ApiResponse<{ url: string }>> {
  return apiPost<ApiResponse<{ url: string }>>("/stripe/checkout", body as Record<string, unknown>);
}

export async function createBillingPortalSession(body: {
  venueId: string;
}): Promise<ApiResponse<{ url: string }>> {
  return apiPost<ApiResponse<{ url: string }>>("/stripe/billing-portal", body as Record<string, unknown>);
}

// Legacy class-based client for backwards compatibility
export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}
